import styled from 'styled-components/macro';

import Button from 'components/Button/Button';
import ButtonIcon from 'components/ButtonIcon/ButtonIcon';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';

export const ControlsWrapStyled = styled(FlexContainer)`
  margin-top: 1rem;
  margin-bottom: 1rem;

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      margin-top: 1rem;
    }
  `}
`;

export const ActionBtnStyled = styled(Button)`
  height: 48px;
  flex: 1 1;
  font-size: ${({ theme }): string => theme.fontSize['2xl']};
  font-weight: ${({ theme }): string => theme.fontWeight.semibold.toString()};

  ${({ theme }): string => `
    ${theme.devices.tablet} {    
      width: 98px;
      height: 38px;
      flex: none;
      font-size: ${theme.fontSize.lg};
    }
  `}
`;
export const TypographyStyled = styled(Typography)`
  margin-top: 1rem;
`;

export const FlexContainerMessageStyled = styled(FlexContainer)`
  ${({ theme }): string => `
    ${theme.devices.tablet} {
      padding: 0 2.5rem 0.625rem;
    }
  `}
`;

export const TypographyTitleStyled = styled(Typography)`
  margin-bottom: 1.875rem;
`;

export const FormStyled = styled.form`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      width: 100%;
    }
  `}
`;

/** Wrapper to center and constrain form content width within the card. */
export const FormContentWrapStyled = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
`;

/** Form field spacing: increased vertical space between entry fields. */
export const FormElementWrapStyled = styled.div`
  margin-bottom: 1.25rem;
`;

export const getPopupContentStyle = (
  isMobile: boolean,
  isDesktop: boolean
): Record<string, string> => ({
  width: '100%',
  height: isMobile ? '100%' : 'auto',
  minWidth: isMobile ? 'auto' : '600px',
  maxWidth: '1100px',
  minHeight: isDesktop ? '540px' : '0',
  maxHeight: isMobile ? '100%' : '95vh',
  padding: isMobile ? '0.625rem 1.25rem 1.375rem' : '0.625rem 1.875rem 1.375rem',
  overflow: 'auto',
});

/** Page layout wrapper: fills available height, no scrollbar (flex child of layout). */
export const TicketDetailPageWrapStyled = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

/**
 * Top header – matches WarrQueue WorkItem header:
 * paddingX: 2 (16px), paddingBottom: 1 (8px), content mt: 2 (16px) via inner wrapper.
 * Shadow only on this row so it falls above the cards row below.
 */
export const TicketDetailHeaderRowWithShadowStyled = styled.div`
  position: relative;
  padding: 16px 16px 8px;
  box-sizing: border-box;
  background-color: ${({ theme }): string => theme.colors.surface};
  box-shadow: ${({ theme }): string => theme.shadows.variant2};
  z-index: 1;
`;

/**
 * Wraps header row + cards row (no shadow; shadow is on header row only).
 */
export const TicketDetailHeaderWrapStyled = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: ${({ theme }): string => theme.colors.surface};
`;

/**
 * Row below header, above chatbox – matches WarrQueue LineItemTabs row (bot card + tickets card).
 * Contains schedule card, editable fields, and save button.
 */
export const TicketDetailsCardsRowStyled = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 16px;
  min-height: 56px;
  border-top: 1px solid ${({ theme }): string => theme.colors?.primary?.variant5 ?? '#e0e0e0'};
  background-color: ${({ theme }): string => theme.colors.surface};
  box-sizing: border-box;
  flex-shrink: 0;
`;

/** Left/middle section of cards row: schedule card + form fields (gap matches WarrQueue RepairCard row). */
export const TicketDetailsCardsRowLeftStyled = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  gap: 6px;
  flex-wrap: wrap;
`;

/** Right section of cards row: save button (like action buttons in WarrQueue row). */
export const TicketDetailsCardsRowRightStyled = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

/**
 * Cards row field wrapper – Straight Time style (WarrQueue RepairCard).
 * Width 100px, size small, 13px font, horizontally aligned.
 */
export const CardsRowFieldStraightTimeStyled = styled.div`
  flex: 0 0 auto;
  width: 100px;
  min-width: 100px;
  padding-top: 9px;
  box-sizing: border-box;
  & .MuiTextField-root,
  & .MuiFormControl-root {
    width: 100% !important;
    min-width: 0 !important;
  }
  & .MuiInputBase-input,
  & .MuiSelect-select {
    font-size: 13px !important;
  }
  & .MuiSelect-select {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }
  & .MuiOutlinedInput-root {
    height: auto !important;
  }
  & .MuiInputLabel-root {
    font-size: 13px;
  }
  & .MuiFormHelperText-root {
    font-size: 11px;
  }
`;

/**
 * Cards row field wrapper – Repair Date style (WarrQueue RepairCard).
 * Width 200px; for Claim Status and Claim Submitted Date.
 */
export const CardsRowFieldRepairDateStyled = styled.div`
  flex: 0 0 auto;
  width: 200px;
  min-width: 200px;
  padding-top: 9px;
  box-sizing: border-box;
  & .MuiTextField-root,
  & .MuiFormControl-root {
    width: 100% !important;
    min-width: 0 !important;
  }
  & .MuiInputBase-input,
  & .MuiSelect-select {
    font-size: 13px !important;
  }
  & .MuiSelect-select {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }
  & .MuiOutlinedInput-root {
    height: auto !important;
  }
  & .MuiInputLabel-root {
    font-size: 13px;
  }
  & .MuiFormHelperText-root {
    font-size: 11px;
  }
`;

/**
 * Cards row field wrapper – Priority style (WarrQueue RepairCard priority dropdown).
 * Width 65px, icon-only select padding, 13px font.
 */
export const CardsRowFieldPriorityStyled = styled.div`
  flex: 0 0 auto;
  width: 65px;
  min-width: 65px;
  padding-top: 9px;
  box-sizing: border-box;
  & .MuiFormControl-root {
    width: 100% !important;
    min-width: 0 !important;
  }
  & .MuiSelect-select {
    padding-right: 32px !important;
    padding-left: 14px !important;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    font-size: 13px !important;
    display: flex !important;
    align-items: center !important;
  }
  & .MuiOutlinedInput-root {
    height: auto !important;
  }
  & .MuiInputLabel-root {
    font-size: 13px;
  }
  & .MuiInputLabel-shrink {
    transform: translate(14px, -6px) scale(0.75);
  }
`;

/** Content area: 4px gap from header, sides, and bottom. */
export const TicketDetailContentWrapStyled = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 4px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }): string => theme.colors.primary.variant2};
`;

/**
 * Expandable left card (like WarrQueue Repair Card): chevron to expand/collapse.
 * Width increased by 25% from original (350px expanded, 300px min).
 */
export const TicketDetailLeftCardStyled = styled.div<{ $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  width: ${({ $expanded }): string => ($expanded ? '350px' : '56px')};
  min-width: ${({ $expanded }): string => ($expanded ? '300px' : '56px')};
  min-height: 0;
  background-color: ${({ theme }): string => theme.colors.surface};
  border-radius: 12px;
  box-shadow: ${({ theme }): string => theme.shadows.variant1};
  box-sizing: border-box;
  overflow: hidden;
  transition: width 0.3s ease-in-out, min-width 0.3s ease-in-out;

  ${({ theme, $expanded }): string => `
    ${theme.devices.tablet} {
      min-height: 300px;
      max-width: ${$expanded ? '650px' : '56px'};
    }
  `}
`;

/** Header row: title + chevron (like Repair Card). */
export const TicketDetailLeftCardHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  min-height: 40px;
  padding: 4px 8px 6px;
  box-sizing: border-box;
`;

/** Collapsed state: centered icon area. */
export const TicketDetailLeftCardCollapsedStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  padding-top: 16px;
  min-height: 150px;
  box-sizing: border-box;
`;

/** Expanded state: scroll area + footer (Save at bottom right). */
export const TicketDetailLeftCardContentStyled = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 8px 16px 16px;
  box-sizing: border-box;
`;

/** Scrollable area for Schedule + form fields. */
export const TicketDetailLeftCardScrollStyled = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  gap: 12px;
  box-sizing: border-box;

  & > * {
    width: 100%;
    min-width: 0;
    max-width: none;
    flex: 0 0 auto;
  }
`;

/** Footer row: Save button aligned right. */
export const TicketDetailLeftCardFooterStyled = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  padding-top: 12px;
  box-sizing: border-box;
`;

/**
 * Expandable right card – mirror of left Details card. Attachments card.
 */
export const TicketDetailRightCardStyled = styled.div<{ $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  width: ${({ $expanded }): string => ($expanded ? '350px' : '56px')};
  min-width: ${({ $expanded }): string => ($expanded ? '300px' : '56px')};
  min-height: 0;
  background-color: ${({ theme }): string => theme.colors.surface};
  border-radius: 12px;
  box-shadow: ${({ theme }): string => theme.shadows.variant1};
  box-sizing: border-box;
  overflow: hidden;
  transition: width 0.3s ease-in-out, min-width 0.3s ease-in-out;

  ${({ theme, $expanded }): string => `
    ${theme.devices.tablet} {
      min-height: 300px;
      max-width: ${$expanded ? '650px' : '56px'};
    }
  `}
`;

/** Right card header row: title + chevron. */
export const TicketDetailRightCardHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  min-height: 40px;
  padding: 4px 8px 6px;
  box-sizing: border-box;
`;

/** Right card collapsed: centered icon. */
export const TicketDetailRightCardCollapsedStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  padding-top: 16px;
  min-height: 150px;
  box-sizing: border-box;
`;

/** Right card expanded: scrollable content. */
export const TicketDetailRightCardContentStyled = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 16px 16px;
  box-sizing: border-box;
`;

/** Right card scroll area. */
export const TicketDetailRightCardScrollStyled = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  gap: 12px;
  box-sizing: border-box;

  & > * {
    width: 100%;
    min-width: 0;
    max-width: none;
    flex: 0 0 auto;
  }
`;

/** Inner content row: left card + messages + right card (4px between); no scrollbar. */
export const TicketDetailBodyRowStyled = styled.div`
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  gap: 4px;

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      flex-direction: row;
      gap: 4px;
    }
  `}
`;

/** Messages column: fills remaining space. */
export const TicketDetailMessagesColStyled = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

/** Card wrapping chat list + send form (same style as left card; 12px radius; only inner list scrolls). */
export const TicketDetailMessagesCardStyled = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background-color: ${({ theme }): string => theme.colors.surface};
  border-radius: 12px;
  box-shadow: ${({ theme }): string => theme.shadows.variant1};
  box-sizing: border-box;
`;

/**
 * Single header row – stats | form fields + schedule | controls. Allows wrap for fields.
 */
export const HeaderStyled = styled.header`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
  overflow-x: auto;
`;

/**
 * Stats block – condensed gap, no wrap.
 */
export const HeaderInfoStyled = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 20px;
  min-width: 0;
`;

/**
 * Header middle: form fields (Amount, Claim Status, etc.) then schedule card at end.
 */
export const HeaderFieldsAndScheduleStyled = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
`;

/**
 * Wrapper for each header stat – matches WarrQueue Indicators: compact, column, label above value.
 */
export const HeaderStatBlockStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  max-width: 120px;
  min-width: 0;
`;

/** Dealer stat block: double width of default (240px). */
export const HeaderStatBlockDealerStyled = styled(HeaderStatBlockStyled)`
  max-width: 240px;
`;

/** Category stat block: more space for long names, no wrap. */
export const HeaderStatBlockCategoryStyled = styled(HeaderStatBlockStyled)`
  max-width: 240px;
  min-width: 120px;
  & > *:last-child {
    white-space: nowrap;
  }
`;

export const HeaderControlsStyled = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const CopyIconBtnStyled = styled(ButtonIcon)`
  margin-left: 0.375rem;
`;

export const CloseIconBtnStyled = styled(ButtonIcon)`
  margin-left: 0.25rem;
`;

export const ContentStyled = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      flex-direction: row;
    }
  `}
`;

export const ContentInfoStyled = styled.div`
  display: flex;

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      flex-direction: column;
      margin-right: 1.25rem;
    }

    ${theme.devices.smallDesktop} {
      margin-right: 3.75rem;
    }
  `}
`;

export const ContentInfoItemStyled = styled.div`
  &:not(:last-child) {
    margin-bottom: 1.25rem;
    margin-right: 1.5rem;
  }

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      &:not(:last-child) {
        margin-right: 0;
      }
    }
  `}
`;

export const ContentInfoTypographyStyled = styled(Typography)`
  margin-bottom: 0.125rem;
`;

/** Wrapper for messages in popup layout (flex so inner card layout fills height). */
export const MessagesWrapStyled = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const FlexStyled = styled(FlexContainer)`
  height: 100%;

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      height: 494px;
    }
  `}
`;

/**
 * Header stat value – matches WarrQueue work item header (ROStats/Indicators): 13px, theme text color.
 */
export const HeaderStatTypographyStyled = styled(Typography)`
  font-size: 13px;
  line-height: 1.5;
`;

/**
 * Label above stat value – matches WarrQueue work item header (Indicators/Subheader): 12px, grey[600].
 */
export const FieldLabelStyled = styled.label`
  display: block;
  white-space: nowrap;
  margin-bottom: 2px;
  font-size: 12px;
  line-height: 1.43;
  color: ${({ theme }): string => theme.colors.primary.variant8};
`;

export const ActionIconButtonStyled = styled(ButtonIcon)`
  margin-left: 0.1rem;
`;

export const ScheduleHeaderLabelStyled = styled.label`
  display: block;
  white-space: nowrap;
  font-size: ${({ theme }): string => theme.fontSize.xl};
  color: ${({ theme }): string => theme.colors.primary.variant9};
`;

export const FlexContainerScheduleStyled = styled(FlexContainer)`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${FormElementWrapStyled} {
    width: 100%;
  }
  ${ScheduleHeaderLabelStyled} {
    align-self: start;
    margin-bottom: -13px;
  }
`;

export const ScheduleInfoWrapStyled = styled(FlexContainer)`
  margin-bottom: 0.5rem;
  width: 100%;
  ${TypographyStyled} {
    margin-right: 10px;
  }
`;

/** Small card in header/details: Schedule title centered, 3 fields below. Condensed padding. */
export const ScheduleCardStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 8px 6px;
  border: 1px solid ${({ theme }): string => theme.colors.primary.variant6};
  border-radius: 6px;
  background-color: ${({ theme }): string => theme.colors.surface};
  flex-shrink: 0;
`;

/** Schedule card title – compact, centered. */
export const ScheduleCardTitleStyled = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }): string => theme.colors.primary.variant9};
  margin-bottom: 4px;
  text-align: center;
`;

/** Row of schedule read-only fields – condensed gap. */
export const ScheduleCardFieldsRowStyled = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  gap: 6px;
  width: 100%;
  min-width: 0;
`;

export const ScheduleCardFieldStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }): string => theme.fontSize.sm};
  color: ${({ theme }): string => theme.colors.primary.variant11};
`;

/**
 * Form fields in header – single row, condensed gap (WarrQueue header density), no wrap.
 */
export const HeaderFieldsRowStyled = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 6px;
  flex: 0 0 auto;
  min-width: 0;
`;

/**
 * Wrapper for each header form field – matches WarrQueue ROStats: ~84–90px width, size small, 13px, dense padding.
 */
export const HeaderFieldWrapStyled = styled.div`
  min-width: 0;
  flex: 0 0 auto;
  width: 90px;
  max-width: 90px;
  & .MuiTextField-root,
  & .MuiFormControl-root {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 90px;
  }
  & .MuiInputBase-root {
    padding-top: 0;
    padding-bottom: 0;
    font-size: 13px;
  }
  & .MuiInputBase-input {
    font-size: 13px;
    padding: 4px 8px;
    box-sizing: border-box;
  }
  & .MuiInputLabel-root {
    font-size: 13px;
    transform: translate(14px, 7px) scale(1);
  }
  & .MuiInputLabel-root.MuiInputLabel-shrink {
    transform: translate(14px, -9px) scale(0.75);
  }
  & .MuiOutlinedInput-root {
    padding-top: 0;
    padding-bottom: 0;
  }
  & .MuiFormHelperText-root {
    font-size: 10px;
    margin-top: 2px;
  }
`;
